import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import {
  axiosInstance,
  useClientInfo,
  useAppContext,
} from "../utils/commonImports";
import { getFYRange, resolveDateRange } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

/* Amount columns shown before the GST columns */
const AMOUNT_COLS = [
  { key: "basic_amount", label: "Basic", width: 90 },
  { key: "escort_amount", label: "Escort", width: 80 },
  { key: "batta_amount", label: "Batta", width: 80 },
  { key: "parking_amount", label: "Parking", width: 80 },
  { key: "others_amount", label: "Others", width: 80 },
  { key: "permit_amount", label: "Permit", width: 80 },
  { key: "toll_amount", label: "Toll", width: 80 },
  { key: "fuel_esc_amount", label: "Fuel", width: 80 },
  { key: "addition_amount", label: "Addition", width: 90 },
  { key: "discount", label: "Discount", width: 90 },
];

const num = (v) => Number(v || 0).toFixed(2);

const isRCMRow = (r) => r.gst_treatment?.toLowerCase().includes("rcm");

const datePickerSx = {
  width: 160,
  "& .MuiInputBase-root": {
    height: 34,
    fontSize: "0.75rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
  },
};

export default function PurchaseRegister() {
  const navigate = useNavigate();
  const { clientId } = useClientInfo();

  // Financial year selected in the header
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const fy = selectedFinancialYear?.label || ""; // e.g. "2025-26"
  const { start: fyStart, end: fyEnd } = getFYRange(selectedFinancialYear);

  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [vendor, setVendor] = useState("ALL");

  const [vehicles, setVehicles] = useState([]);
  const [vehicle, setVehicle] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [searchVersion, setSearchVersion] = useState(0);

  /* ================= BACK BUTTON -> Reports dashboard ================= */
  const handleBack = () => navigate(REPORTS_PATH);

  /* ================= LOAD VENDORS ================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/vendors", { params: { client_id: clientId } })
      .then((res) => setVendors(res.data || []))
      .catch(console.error);
  }, [clientId]);

  /* ================= LOAD VEHICLES ================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/vehicles", { params: { client_id: clientId } })
      .then((res) => setVehicles(res.data || []))
      .catch(console.error);
  }, [clientId]);

  const handleSearch = () => setSearchVersion((version) => version + 1);
  const handlePrint = () => window.print();
  // Opens the print dialog; select "Save as PDF" to create a PDF.
  const handlePdf = () => window.print();

  const handleExcel = () => {
    const headers = [
      "Bill No", "Date", "Party",
      ...AMOUNT_COLS.map((column) => column.label),
      "IGST %", "CGST %", "SGST %",
      "GST IGST", "GST CGST", "GST SGST",
      "RCM IGST", "RCM CGST", "RCM SGST", "Net",
    ];

    const csvEscape = (value) => {
      const text = value == null ? "" : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const csvData = rows.map((row) => {
      const rcm = isRCMRow(row);
      return [
        row.purchase_number,
        row.purchase_date ? dayjs(row.purchase_date).format("DD/MM/YYYY") : "",
        row.party_name,
        ...AMOUNT_COLS.map((column) => row[column.key]),
        row.igst_percent, row.cgst_percent, row.sgst_percent,
        rcm ? 0 : row.igst_amount,
        rcm ? 0 : row.cgst_amount,
        rcm ? 0 : row.sgst_amount,
        rcm ? row.igst_amount : 0,
        rcm ? row.cgst_amount : 0,
        rcm ? row.sgst_amount : 0,
        row.net_amount,
      ];
    });

    const csv = "\\uFEFF" + [
      headers.map(csvEscape).join(","),
      ...csvData.map((row) => row.map(csvEscape).join(",")),
    ].join("\\r\\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Purchase_Register_${fy || "Report"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /* ================= FY CHANGED: clear manual dates ================= */
  useEffect(() => {
    setFromDate("");
    setToDate("");
  }, [selectedFinancialYear?.fyid]);

  /* ================= LOAD DATA (FY aware) ================= */
  useEffect(() => {
    if (!clientId || financialYearLoading) return;

    let cancelled = false;

    // picked dates win, but are clamped inside the selected FY
    const { from, to } = resolveDateRange(
      selectedFinancialYear,
      fromDate,
      toDate,
    );

    setLoading(true);
    setReportError("");

    axiosInstance
      .get("/api/purchases/purchase-register", {
        params: {
          client_id: clientId,
          fromDate: from || null,
          toDate: to || null,
          fyid: selectedFinancialYear?.fyid || "",
          fy_id: selectedFinancialYear?.fyid || "",
          fy,
          vendor_id: vendor === "ALL" ? null : vendor,
          vehicle_id: vehicle === "ALL" ? null : vehicle,
        },
      })
      .then((res) => {
        if (!cancelled) {
          const data = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
              ? res.data.data
              : [];
          setRows(data);
        }
      })
      .catch((err) => {
        console.error("Purchase Register load error:", err.response?.data || err);
        if (!cancelled) {
          setRows([]);
          setReportError(
            err.response?.data?.message ||
            err.message ||
            "Failed to load Purchase Register."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // ignore a slow response from an older request
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    fromDate,
    toDate,
    vendor,
    vehicle,
    selectedFinancialYear?.fyid,
    financialYearLoading,
    searchVersion,
  ]);

  /* ================= TOTALS ================= */
  const sum = (field) => rows.reduce((t, r) => t + Number(r[field] || 0), 0);

  const rcmRows = rows.filter(isRCMRow);
  const sumRcm = (field) =>
    rcmRows.reduce((t, r) => t + Number(r[field] || 0), 0);

  const totals = {
    igst: sum("igst_amount"),
    cgst: sum("cgst_amount"),
    sgst: sum("sgst_amount"),
    net: sum("net_amount"),
    rcm_igst: sumRcm("igst_amount"),
    rcm_cgst: sumRcm("cgst_amount"),
    rcm_sgst: sumRcm("sgst_amount"),
  };

  const grouped = vendor === "ALL" && vehicle === "ALL";

  /* ================= DATA ROW ================= */
  const renderRow = (r, i) => {
    const isRCM = isRCMRow(r);

    return (
      <TableRow
        key={i}
        sx={{
          backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa",
          "& td": {
            fontSize: "0.70rem",
            border: "1px solid #e0e0e0",
          },
        }}
      >
        <TableCell>{r.purchase_number}</TableCell>
        <TableCell>{dayjs(r.purchase_date).format("DD/MM/YYYY")}</TableCell>
        <TableCell>{r.party_name}</TableCell>

        {AMOUNT_COLS.map((c) => (
          <TableCell key={c.key} align="right">
            {num(r[c.key])}
          </TableCell>
        ))}

        {/* GST % */}
        <TableCell align="right">{num(r.igst_percent)}</TableCell>
        <TableCell align="right">{num(r.cgst_percent)}</TableCell>
        <TableCell align="right">{num(r.sgst_percent)}</TableCell>

        {/* NORMAL GST */}
        <TableCell align="right">{isRCM ? "0.00" : num(r.igst_amount)}</TableCell>
        <TableCell align="right">{isRCM ? "0.00" : num(r.cgst_amount)}</TableCell>
        <TableCell align="right">{isRCM ? "0.00" : num(r.sgst_amount)}</TableCell>

        {/* RCM GST */}
        <TableCell align="right">{isRCM ? num(r.igst_amount) : "0.00"}</TableCell>
        <TableCell align="right">{isRCM ? num(r.cgst_amount) : "0.00"}</TableCell>
        <TableCell align="right">{isRCM ? num(r.sgst_amount) : "0.00"}</TableCell>

        <TableCell align="right" sx={{ fontWeight: 600 }}>
          {num(r.net_amount)}
        </TableCell>
      </TableRow>
    );
  };

  const headerRowSx = {
    "& th": {
      backgroundColor: "#eeeeee",
      fontWeight: 700,
      fontSize: "0.72rem",
      border: "1px solid #e0e0e0",
      textAlign: "center",
      whiteSpace: "nowrap",
    },
  };

  return (
    <Box
      p={2}
      sx={{
        "@media print": {
          "& .no-print": { display: "none !important" },
          "& .MuiPaper-root": { boxShadow: "none !important" },
          "& .MuiTableContainer-root": {
            maxHeight: "none !important",
            overflow: "visible !important",
          },
          "& table": { minWidth: "0 !important" },
          "& th, & td": { fontSize: "8px !important", padding: "3px !important" },
        },
      }}
    >
      {/* HEADER WITH BACK BUTTON */}
      <Box
        className="purchase-report-header no-print"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        mb={2}
        flexWrap="wrap"
      >
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate(REPORTS_PATH)}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Purchase Register
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Purchase Register
            {fy && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                FY {fy}
              </Typography>
            )}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch} disabled={loading || financialYearLoading || !clientId}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}>
            Search
          </Button>
          <Button variant="outlined" color="primary" startIcon={<PrintIcon />}
            onClick={handlePrint} disabled={loading || rows.length === 0}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}>
            Print
          </Button>
          <Button variant="outlined" color="error" startIcon={<PictureAsPdfIcon />}
            onClick={handlePdf} disabled={loading || rows.length === 0}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}>
            PDF
          </Button>
          <Button variant="outlined" color="success" startIcon={<FileDownloadIcon />}
            onClick={handleExcel} disabled={loading || rows.length === 0}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}>
            Excel
          </Button>
        </Box>
      </Box>

      {/* FILTER BAR */}
      <Box
        className="no-print"
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
          background: "#fafafa",
          border: "1px solid #ddd",
          borderRadius: 1,
          px: 2,
          py: 1.2,
          mb: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            format="DD/MM/YYYY"
            value={fromDate ? dayjs(fromDate) : null}
            minDate={fyStart ? dayjs(fyStart) : undefined}
            maxDate={fyEnd ? dayjs(fyEnd) : undefined}
            onChange={(newValue) =>
              setFromDate(newValue ? newValue.format("YYYY-MM-DD") : "")
            }
            slotProps={{ textField: { size: "small", sx: datePickerSx } }}
          />

          <DatePicker
            label="To Date"
            format="DD/MM/YYYY"
            value={toDate ? dayjs(toDate) : null}
            minDate={fyStart ? dayjs(fyStart) : undefined}
            maxDate={fyEnd ? dayjs(fyEnd) : undefined}
            onChange={(newValue) =>
              setToDate(newValue ? newValue.format("YYYY-MM-DD") : "")
            }
            slotProps={{ textField: { size: "small", sx: datePickerSx } }}
          />
        </LocalizationProvider>

        <FormControl
          size="small"
          sx={{
            width: 240,
            "& .MuiInputBase-root": {
              height: 34,
              fontSize: "0.75rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.75rem",
            },
          }}
        >
          <InputLabel>Vendor</InputLabel>
          <Select
            value={vendor}
            label="Vendor"
            onChange={(e) => setVendor(e.target.value)}
          >
            <MenuItem value="ALL">All Vendors</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v.vendorid} value={v.vendorid}>
                {v.vendorname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{
            width: 240,
            "& .MuiInputBase-root": {
              height: 34,
              fontSize: "0.75rem",
            },
          }}
        >
          <InputLabel>Vehicle</InputLabel>
          <Select
            value={vehicle}
            label="Vehicle"
            onChange={(e) => setVehicle(e.target.value)}
          >
            <MenuItem value="ALL">All Vehicles</MenuItem>

            {vehicles.map((vh) => (
              <MenuItem key={vh.vehicle_id} value={vh.vehicle_id}>
                {vh.vehicle_number}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" startIcon={<SearchIcon />}
          onClick={handleSearch} disabled={loading || financialYearLoading || !clientId}
          sx={{ height: 40, textTransform: "uppercase" }}>
          Search
        </Button>
      </Box>

      {reportError && (
        <Alert severity="error" sx={{ mt: 1, mb: 1 }} className="no-print">
          {reportError}
        </Alert>
      )}

      {loading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }} className="no-print">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading Purchase Register...
          </Typography>
        </Box>
      )}

      <Paper>
        <TableContainer sx={{ maxHeight: "75vh" }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: "fixed",
              minWidth: 1700,
              "& th, & td": {
                border: "1px solid #ccc",
                fontSize: "0.70rem",
                padding: "4px 6px",
                whiteSpace: "nowrap",
              },
            }}
          >
            {/* HEADER */}
            <TableHead>
              <TableRow sx={headerRowSx}>
                <TableCell rowSpan={2} sx={{ width: 110 }}>
                  Bill No
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 90 }}>
                  Date
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 220 }}>
                  Party
                </TableCell>

                {AMOUNT_COLS.map((c) => (
                  <TableCell key={c.key} rowSpan={2} sx={{ width: c.width }}>
                    {c.label}
                  </TableCell>
                ))}

                <TableCell colSpan={3} sx={{ width: 150 }}>
                  GST %
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  GST Amount
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  RCM GST
                </TableCell>

                <TableCell rowSpan={2} sx={{ width: 110 }}>
                  Net
                </TableCell>
              </TableRow>

              <TableRow sx={headerRowSx}>
                <TableCell sx={{ width: 50 }}>IGST%</TableCell>
                <TableCell sx={{ width: 50 }}>CGST%</TableCell>
                <TableCell sx={{ width: 50 }}>SGST%</TableCell>

                <TableCell sx={{ width: 70 }}>IGST</TableCell>
                <TableCell sx={{ width: 70 }}>CGST</TableCell>
                <TableCell sx={{ width: 70 }}>SGST</TableCell>

                <TableCell sx={{ width: 70 }}>IGST</TableCell>
                <TableCell sx={{ width: 70 }}>CGST</TableCell>
                <TableCell sx={{ width: 70 }}>SGST</TableCell>
              </TableRow>
            </TableHead>

            {/* BODY */}
            <TableBody>
              {(grouped
                ? Object.entries(
                    rows.reduce((acc, row) => {
                      const key = row.party_name || "Unknown";
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(row);
                      return acc;
                    }, {}),
                  )
                : [["DATA", rows]]
              ).map(([groupName, groupRows], idx) => {
                const sub = (field) =>
                  groupRows.reduce((t, r) => t + Number(r[field] || 0), 0);

                return (
                  <React.Fragment key={idx}>
                    {/* GROUP TITLE ONLY WHEN BOTH ALL */}
                    {grouped && (
                      <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                        <TableCell
                          colSpan={23}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.80rem",
                            padding: "8px",
                          }}
                        >
                          {groupName}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* DATA ROWS */}
                    {groupRows.map(renderRow)}

                    {/* SUBTOTAL WHEN BOTH ALL */}
                    {grouped && (
                      <TableRow
                        sx={{
                          backgroundColor: "#f1f2f6",
                          "& td": {
                            fontWeight: 700,
                            borderTop: "2px solid #e0e0e0",
                            borderBottom: "1px solid #e0e0e0",
                          },
                        }}
                      >
                        <TableCell colSpan={3}>Subtotal</TableCell>

                        {AMOUNT_COLS.map((c) => (
                          <TableCell key={c.key} align="right">
                            {num(sub(c.key))}
                          </TableCell>
                        ))}

                        <TableCell colSpan={3}></TableCell>

                        <TableCell align="right">
                          {num(sub("igst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("cgst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("sgst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("rcm_igst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("rcm_cgst_amount"))}
                        </TableCell>
                        <TableCell align="right">
                          {num(sub("rcm_sgst_amount"))}
                        </TableCell>

                        <TableCell align="right">
                          {num(sub("net_amount"))}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={23} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            {/* FOOTER */}
            <TableFooter>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& td": {
                    fontWeight: 700,
                    borderTop: "2px solid #bbbaba",
                    borderBottom: "2px solid #e0e0e0",
                    fontSize: "0.75rem",
                  },
                }}
              >
                <TableCell colSpan={3}>TOTAL</TableCell>

                {AMOUNT_COLS.map((c) => (
                  <TableCell key={c.key} align="right">
                    {num(sum(c.key))}
                  </TableCell>
                ))}

                <TableCell colSpan={3}></TableCell>

                <TableCell align="right">{num(totals.igst)}</TableCell>
                <TableCell align="right">{num(totals.cgst)}</TableCell>
                <TableCell align="right">{num(totals.sgst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_igst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_cgst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_sgst)}</TableCell>

                <TableCell align="right">{num(totals.net)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
